import React, { useState, useRef, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const QRScanner = ({ onScanResult, isActive, onToggle }) => {
  const [scannerState, setScannerState] = useState('idle'); // idle, scanning, processing, error
  const [cameraPermission, setCameraPermission] = useState('prompt');
  const [errorMessage, setErrorMessage] = useState('');
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  const mockCredentials = [
    {
      id: "cred_001",
      type: "EducationalCredential",
      issuer: "Stanford University",
      subject: "John Smith",
      issuanceDate: "2024-01-15",
      expirationDate: "2029-01-15",
      credentialSubject: {
        degree: "Master of Science",
        field: "Computer Science",
        gpa: "3.85"
      }
    },
    {
      id: "cred_002", 
      type: "ProfessionalCertification",
      issuer: "AWS Certification Authority",
      subject: "Sarah Johnson",
      issuanceDate: "2024-06-20",
      expirationDate: "2027-06-20",
      credentialSubject: {
        certification: "AWS Solutions Architect",
        level: "Professional",
        score: "892"
      }
    }
  ];

  const startCamera = async () => {
    try {
      setScannerState('scanning');
      setErrorMessage('');
      
      const stream = await navigator.mediaDevices?.getUserMedia({
        video: { 
          facingMode: 'environment',
          width: { ideal: 1280 },
          height: { ideal: 720 }
        }
      });
      
      streamRef.current = stream;
      if (videoRef?.current) {
        videoRef.current.srcObject = stream;
        videoRef?.current?.play();
      }
      setCameraPermission('granted');
    } catch (error) {
      console.error('Camera access error:', error);
      setCameraPermission('denied');
      setScannerState('error');
      setErrorMessage('Camera access denied. Please enable camera permissions and try again.');
    }
  };

  const stopCamera = () => {
    if (streamRef?.current) {
      streamRef?.current?.getTracks()?.forEach(track => track?.stop());
      streamRef.current = null;
    }
    if (videoRef?.current) {
      videoRef.current.srcObject = null;
    }
    setScannerState('idle');
  };

  const simulateQRScan = () => {
    setScannerState('processing');
    
    // Simulate QR code detection and processing
    setTimeout(() => {
      const randomCredential = mockCredentials?.[Math.floor(Math.random() * mockCredentials?.length)];
      onScanResult({
        type: 'qr_scan',
        credential: randomCredential,
        scanTimestamp: new Date()?.toISOString(),
        scanMethod: 'camera'
      });
      setScannerState('idle');
      stopCamera();
    }, 2000);
  };

  const handleToggleScanner = () => {
    if (isActive) {
      stopCamera();
      onToggle(false);
    } else {
      onToggle(true);
      startCamera();
    }
  };

  useEffect(() => {
    return () => {
      stopCamera();
    };
  }, []);

  useEffect(() => {
    if (isActive && scannerState === 'scanning') {
      // Simulate automatic QR detection after 3 seconds
      const timer = setTimeout(() => {
        if (scannerState === 'scanning') {
          simulateQRScan();
        }
      }, 3000);
      
      return () => clearTimeout(timer);
    }
  }, [isActive, scannerState]);

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      {/* Scanner Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <Icon name="QrCode" size={20} className="text-primary mr-2" />
            <h3 className="font-semibold text-foreground">QR Code Scanner</h3>
          </div>
          <div className="flex items-center space-x-2">
            <div className={`w-2 h-2 rounded-full ${
              scannerState === 'scanning' ? 'bg-success animate-pulse' :
              scannerState === 'processing' ? 'bg-warning animate-pulse' :
              scannerState === 'error' ? 'bg-destructive' : 'bg-muted'
            }`} />
            <span className="text-sm text-muted-foreground capitalize">
              {scannerState === 'processing' ? 'Processing...' : scannerState}
            </span>
          </div>
        </div>
      </div>
      {/* Scanner Interface */}
      <div className="relative">
        {!isActive ? (
          /* Scanner Inactive State */
          (<div className="aspect-video bg-muted flex flex-col items-center justify-center p-8">
            <div className="w-24 h-24 border-2 border-dashed border-muted-foreground/30 rounded-lg flex items-center justify-center mb-4">
              <Icon name="Camera" size={32} className="text-muted-foreground/50" />
            </div>
            <h4 className="font-medium text-foreground mb-2">Ready to Scan</h4>
            <p className="text-sm text-muted-foreground text-center mb-6 max-w-sm">
              Position a QR code within the camera frame to automatically verify the credential
            </p>
            <Button 
              variant="default" 
              onClick={handleToggleScanner}
              iconName="Camera"
              iconPosition="left"
            >
              Start Camera
            </Button>
          </div>)
        ) : (
          /* Active Scanner */
          (<div className="relative aspect-video bg-black">
            <video
              ref={videoRef}
              className="w-full h-full object-cover"
              autoPlay
              muted
              playsInline
            />
            <canvas ref={canvasRef} className="hidden" />
            {/* Scanner Overlay */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="relative">
                {/* Scanning Frame */}
                <div className="w-64 h-64 border-2 border-primary rounded-lg relative">
                  {/* Corner Indicators */}
                  <div className="absolute -top-1 -left-1 w-6 h-6 border-t-4 border-l-4 border-primary rounded-tl-lg" />
                  <div className="absolute -top-1 -right-1 w-6 h-6 border-t-4 border-r-4 border-primary rounded-tr-lg" />
                  <div className="absolute -bottom-1 -left-1 w-6 h-6 border-b-4 border-l-4 border-primary rounded-bl-lg" />
                  <div className="absolute -bottom-1 -right-1 w-6 h-6 border-b-4 border-r-4 border-primary rounded-br-lg" />
                  
                  {/* Scanning Line Animation */}
                  {scannerState === 'scanning' && (
                    <div className="absolute inset-x-0 top-0 h-1 bg-primary animate-pulse" 
                         style={{ 
                           animation: 'scan-line 2s linear infinite',
                           background: 'linear-gradient(90deg, transparent, var(--color-primary), transparent)'
                         }} />
                  )}
                </div>
                
                {/* Processing Indicator */}
                {scannerState === 'processing' && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-lg">
                    <div className="bg-background rounded-lg p-4 flex items-center">
                      <Icon name="Loader2" size={20} className="text-primary animate-spin mr-2" />
                      <span className="text-sm font-medium">Processing QR Code...</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
            {/* Scanner Controls */}
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2">
              <div className="flex items-center space-x-4">
                <Button
                  variant="secondary"
                  size="icon"
                  onClick={handleToggleScanner}
                  className="bg-background/80 hover:bg-background"
                >
                  <Icon name="X" size={20} />
                </Button>
                <Button
                  variant="default"
                  onClick={simulateQRScan}
                  disabled={scannerState === 'processing'}
                  className="bg-primary/90 hover:bg-primary"
                >
                  {scannerState === 'processing' ? 'Processing...' : 'Simulate Scan'}
                </Button>
              </div>
            </div>
          </div>)
        )}

        {/* Error State */}
        {scannerState === 'error' && (
          <div className="absolute inset-0 bg-destructive/10 flex items-center justify-center">
            <div className="bg-background border border-destructive rounded-lg p-4 m-4 max-w-sm">
              <div className="flex items-center mb-2">
                <Icon name="AlertCircle" size={20} className="text-destructive mr-2" />
                <span className="font-medium text-destructive">Scanner Error</span>
              </div>
              <p className="text-sm text-muted-foreground mb-4">{errorMessage}</p>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => {
                  setScannerState('idle');
                  setErrorMessage('');
                }}
              >
                Try Again
              </Button>
            </div>
          </div>
        )}
      </div>
      {/* Scanner Instructions */}
      <div className="p-4 bg-muted/30">
        <div className="flex items-start space-x-3">
          <Icon name="Info" size={16} className="text-primary mt-0.5 flex-shrink-0" />
          <div className="text-sm">
            <p className="text-foreground font-medium mb-1">Scanning Tips:</p>
            <ul className="text-muted-foreground space-y-1">
              <li>• Ensure good lighting conditions</li>
              <li>• Hold device steady and position QR code within frame</li>
              <li>• Keep QR code flat and unobstructed</li>
              <li>• Allow camera to focus automatically</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QRScanner;